import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { spaceApi } from '../services/api';
import { useSpaceId } from '../contexts/SpaceContext';
import type { Email, CreateEmailInput, UpdateEmailInput } from '../types';

function useEmailApi() {
  const spaceId = useSpaceId();
  return useMemo(() => spaceApi(spaceId).emails, [spaceId]);
}

export function useEmails() {
  const spaceId = useSpaceId();
  const api = useEmailApi();
  return useQuery({
    queryKey: ['space', spaceId, 'emails'],
    queryFn: () => api.list(),
  });
}

export function useEmail(emailId: string | undefined) {
  const spaceId = useSpaceId();
  const api = useEmailApi();
  return useQuery({
    queryKey: ['space', spaceId, 'emails', emailId],
    queryFn: () => api.get(emailId!),
    enabled: !!emailId,
  });
}

export function useCreateEmail() {
  const spaceId = useSpaceId();
  const api = useEmailApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEmailInput) => api.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'emails'] });
    },
  });
}

export function useUpdateEmail() {
  const spaceId = useSpaceId();
  const api = useEmailApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ emailId, input }: { emailId: string; input: UpdateEmailInput }) =>
      api.update(emailId, input),
    onSuccess: (data: Email) => {
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'emails'] });
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'emails', data.emailId] });
    },
  });
}

export function useDeleteEmail() {
  const spaceId = useSpaceId();
  const api = useEmailApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (emailId: string) => api.delete(emailId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'emails'] });
    },
  });
}
